/// <amd-module name="Wiki/Editor/AttachmentHelpers" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import * as Resources from "Wiki/Editor/Resources";
import { getFileExtension } from "Wiki/Renderer/VCPathHelper";
import { PathConstants, RepoConstants, UrlEscapeConstants } from "Wiki/ViewCommon/Constants";
import { Attachment, GuidSuffixedFile } from "Wiki/ViewCommon/Contracts";
import { PageErrorType } from "Wiki/ViewCommon/CustomerIntelligenceConstants";

export const MAX_ATTACHMENT_FILE_SIZE = 18 * 1024 * 1024;

export function getMarkdownText(files: GuidSuffixedFile[]): string {
    let markdownText = "";

    for (const file of files) {
        let linkText = `[${file.name}](${_getAttachmentPath(file.guidSuffixedFileName)})`;

        if (file.type && file.type.indexOf("image/") === 0) {
            linkText = `!${linkText}`;
        }

        markdownText += linkText;
    }

    return markdownText;
}

export interface ValidityCategorizedFiles {
    validFiles: GuidSuffixedFile[];
    invalidFileNames: string[];
    invalidFileTypes: string[];
    sizeLimitExceededFiles: string[];
}

export function getValidityCategorizedFiles(pageContext: IVssPageContext, files: File[]): ValidityCategorizedFiles {
    const validFiles: GuidSuffixedFile[] = [];
    const invalidFileNames: string[] = [];
    const invalidFileTypes: string[] = [];
    const sizeLimitExceededFiles: string[] = [];
    for (const file of files) {
        const isValidName: boolean = PathConstants.AttachmentNameReservedCharacters.every((character: string) => {
            return file.name.indexOf(character) === -1;
        });

        if (!isValidName) {
            invalidFileNames.push(file.name);
            continue;
        }

        const fileType = `.${getFileExtension(file.name)}`;
        if (PathConstants.AllowedAttachmentFileTypes(pageContext).indexOf(fileType.toUpperCase()) === -1) {
            invalidFileTypes.push(Utils_String.format('"{0}"', fileType));
            continue;
        }

        if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
            sizeLimitExceededFiles.push(file.name);
            continue;
        }

        /**
         * The attachment names must adhere server friendly path rules.
         * We do not need client friendly path for attachments since we do not render its name to the user anywhere, except for the links.
         */
        const nameWithoutExtension = file.name.substring(0, file.name.lastIndexOf("."));
        const guidSuffixedFile = file as GuidSuffixedFile;
        guidSuffixedFile.guidSuffixedFileName = `${nameWithoutExtension}-${Utils_String.newGuid()}.${getFileExtension(file.name)}`;
        validFiles.push(guidSuffixedFile);
    }

    return {
        validFiles,
        invalidFileNames,
        invalidFileTypes,
        sizeLimitExceededFiles
    };
}

export function handleAttachmentErrors(
    categorizedFiles: ValidityCategorizedFiles,
    errorCallback: (message: string, errorType: PageErrorType) => void
): void {
    // Priority for showing attachment error (when multiple applies) is as below:
    // 1. Individual attachment size exceeds
    // 2. Invalid attachment type
    // 3. Invalid attachment name
    if (categorizedFiles.sizeLimitExceededFiles.length > 0) {
        _handleAttachmentSizeExceedError(categorizedFiles.sizeLimitExceededFiles, errorCallback);
    } else if (categorizedFiles.invalidFileTypes.length > 0) {
        _handleInvalidAttachmentTypeError(categorizedFiles.invalidFileTypes, errorCallback);
    } else if (categorizedFiles.invalidFileNames.length > 0) {
        _handleAttachmentNameInvalidError(categorizedFiles.invalidFileNames, errorCallback);
    }
}

export function getReferredAttachments(content: string, attachmentsMap: { [key: string]: Attachment }): Attachment[] {
    const attachments: Attachment[] = [];
    if (!content) {
        return attachments;
    }

    for (const name of Object.keys(attachmentsMap)) {
        const attachmentPath = `](${_getAttachmentPath(name)}`;
        if (content.indexOf(attachmentPath) >= 0) {
            attachments.push(attachmentsMap[name]);
        }
    }

    return attachments;
}

/**
 * Returns escaped string for attachment name
 * Attachment name should be escaped for markdown to show them as link
 * @param path - name of the file
 * Example: input: some file.png output: some%20file.png
 */
export function getEncodedAttachmentName(path: string): string {
    return path.replace(UrlEscapeConstants.SpaceRegExp, UrlEscapeConstants.SpaceEncoding);
}

export function addAttachments(
    pageContext: IVssPageContext,
    files: File[],
    addAttachmentsCallback: (files: GuidSuffixedFile[], selectionStart: number, selectionEnd: number) => void,
    selectionStart: number,
    selectionEnd: number,
    errorCallback: (message: string, errorType: PageErrorType) => void
): void {
    const categorizedFiles = getValidityCategorizedFiles(pageContext, files);
    handleAttachmentErrors(categorizedFiles, errorCallback);
    addAttachmentsCallback(categorizedFiles.validFiles, selectionStart, selectionEnd);
}

function _getAttachmentPath(guidSuffixedFileName: string): string {
    return `${RepoConstants.AttachmentsFolder}${RepoConstants.RootPath}${getEncodedAttachmentName(guidSuffixedFileName)}`;
}

function _handleInvalidAttachmentTypeError(invalidFileTypes: string[], errorCallback: (message: string, errorType: PageErrorType) => void): void {
    let errorMessage: string;

    if (invalidFileTypes.length === 1) {
        errorMessage = Utils_String.format(Resources.Attachment_NotSupportedFileType, invalidFileTypes[0]);
    } else {
        const lastFileType = invalidFileTypes.pop();
        errorMessage = Utils_String.format(Resources.Attachment_MultipleNotSupportedFileTypes, invalidFileTypes.join(", "), lastFileType);
    }

    errorCallback(errorMessage, PageErrorType.InvalidAttachmentExtension);
}

function _handleAttachmentSizeExceedError(files: string[], errorCallback: (message: string, errorType: PageErrorType) => void): void {
    const attachmentSizeExceedMessage: string = Utils_String.format(
        Resources.Attachment_FileSizeExceeded,
        MAX_ATTACHMENT_FILE_SIZE / (1024 * 1024),
        files.join(", ")
    );
    errorCallback(attachmentSizeExceedMessage, PageErrorType.AttachmentSizeExceeded);
}

function _handleAttachmentNameInvalidError(files: string[], errorCallback: (message: string, errorType: PageErrorType) => void): void {
    const unSupportedCharacters: string[] = [];
    PathConstants.AttachmentNameReservedCharacters.forEach((value: string) => {
        unSupportedCharacters.push(Utils_String.format("'{0}'", value));
    });
    const hasUnSupportedCharactersMessage: string = Utils_String.format(
        Resources.Attachment_UnSupportedCharactersInName,
        unSupportedCharacters.join(", ")
    );
    const errorMessage: string = hasUnSupportedCharactersMessage + files.join(", ");
    errorCallback(errorMessage, PageErrorType.InvalidAttachmentName);
}
